// Pure helpers for the day-view drag-to-reschedule interaction.
//
// IMPORTANT — what this module is NOT:
//   * NOT scheduling logic. It does NOT compute Rule A, Rule B,
//     occupied intervals, or availability. The backend remains the
//     single source of truth — these helpers only describe how a
//     pointer event maps onto the rendered grid.
//   * NOT a DnD framework. Pure functions. No event listeners. No
//     state. No DOM mutations.
//
// Every export is referentially transparent so it can be exhaustively
// unit-tested without React or jsdom.

export const SNAP_MIN = 15;
export const DRAG_THRESHOLD_PX = 4;

/**
 * Snap raw minutes-from-day-start to the nearest 15-minute increment,
 * clamped into the rendered day window [0, dayMinutes - SNAP_MIN].
 *
 * @param {number} rawMinutes — minutes from the start of the day window
 * @param {number} dayMinutes — total height of the day window in minutes
 * @returns {number}
 */
export function snapMinutes(rawMinutes, dayMinutes) {
  if (!Number.isFinite(rawMinutes)) return 0;
  const snapped = Math.round(rawMinutes / SNAP_MIN) * SNAP_MIN;
  const min = 0;
  const max = Math.max(0, dayMinutes - SNAP_MIN);
  return Math.max(min, Math.min(max, snapped));
}

/**
 * Pythagorean distance between two pointer positions.
 * Pure for testability — same maths as `Math.hypot` but null-safe.
 */
export function pointerDistance(ax, ay, bx, by) {
  const dx = (ax || 0) - (bx || 0);
  const dy = (ay || 0) - (by || 0);
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * True iff the cursor has moved far enough from the drag origin to
 * count as a drag (rather than a click). Threshold is intentionally
 * SMALL — staff are quick — but >0 to preserve the click-opens-modal
 * affordance.
 */
export function crossedDragThreshold(startX, startY, currentX, currentY) {
  return pointerDistance(startX, startY, currentX, currentY) >= DRAG_THRESHOLD_PX;
}

/**
 * Is this booking allowed to be dragged at all?
 *
 * Mirrors the existing Reschedule button gating: scheduled bookings
 * yes, completed/cancelled no. Pure — no DOM, no API.
 */
export function isDraggable(booking) {
  if (!booking) return false;
  if (booking.status === "completed" || booking.status === "cancelled") return false;
  return true;
}

/**
 * Given a column rect and the cursor's clientY, derive the snapped
 * minutes-from-day-start the booking would land at if dropped here.
 *
 * Pure — no DOM access, no side effects. Caller is responsible for
 * obtaining the rect (typically via getBoundingClientRect()).
 *
 * Returns null if the cursor is vertically outside the column.
 *
 * @param {{ top: number }} rect — column bounding rect (only `top` used)
 * @param {number} clientY — pointer Y coordinate
 * @param {number} hourPx — pixels per hour in the rendered grid
 * @param {number} hours  — number of hour rows the grid covers
 */
export function resolveSnapMinutes(rect, clientY, hourPx, hours) {
  if (!rect || !Number.isFinite(clientY) || !hourPx || !hours) return null;
  const relY = clientY - rect.top;
  if (relY < 0 || relY > hours * hourPx) return null;
  const rawMinutes = (relY / hourPx) * 60;
  return snapMinutes(rawMinutes, hours * 60);
}

/**
 * Format snapped minutes (from a fixed day-start hour) into a naive
 * ISO datetime string for the given YYYY-MM-DD. Pure.
 *
 * @param {string} ymd — YYYY-MM-DD
 * @param {number} dayStartHour — hour the grid starts at (e.g. 8 → "08:00")
 * @param {number} snappedMin — output of snapMinutes()
 */
export function formatDropIso(ymd, dayStartHour, snappedMin) {
  const totalMin = dayStartHour * 60 + snappedMin;
  const hh = String(Math.floor(totalMin / 60)).padStart(2, "0");
  const mm = String(totalMin % 60).padStart(2, "0");
  return `${ymd}T${hh}:${mm}:00`;
}

/**
 * Inspect a stack of DOM elements at the cursor and decide whether
 * the cursor is currently hovering an OCCUPIED stage segment.
 *
 * Strictly a DOM-attribute read — looks for `data-stage-type="occupied"`
 * on any rendered stage segment in the elements-from-point stack.
 * Returns false for processing stages or empty grid cells.
 *
 * This is the legality HINT used to paint the ghost red. It is NOT a
 * decision — the backend still validates on drop. False negatives
 * (cursor is over the gap between two occupied stages) and false
 * positives (cursor is over the dragged source's own occupied stage)
 * are acceptable: the only contract is "if true, the drop will
 * probably be rejected; if false, no claim is made". The backend
 * always has the final say.
 *
 * @param {Element[]} elementStack — output of document.elementsFromPoint
 * @param {string} [excludeBookingId] — booking currently being dragged;
 *   its own occupied stages should not light up the hint
 * @returns {boolean}
 */
export function legalityHintOverOccupied(elementStack, excludeBookingId = null) {
  if (!Array.isArray(elementStack)) return false;
  for (const el of elementStack) {
    if (!el || !el.dataset) continue;
    if (el.dataset.stageType !== "occupied") continue;
    // The dragged booking's own segments must not trigger the hint —
    // staff dragging a booking onto its own footprint isn't an error.
    const testid = el.dataset.testid || el.getAttribute?.("data-testid") || "";
    if (excludeBookingId && testid.includes(excludeBookingId)) continue;
    return true;
  }
  return false;
}
